import { useState } from 'react';
import type { JobOrder } from '@/domain';
import { usePurgeCase } from '@/application/usecases/useOrders';
import { useSession } from '@/application/usecases/useSession';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import { PurgeCaseDialog } from './PurgeCaseDialog';

/**
 * Delete permanently, and the dialog that asks first. A bare button, so it can
 * sit in any row of actions; nothing for a caller without `approveDeletion`,
 * who are the people who may restore, and nothing on a case that is not
 * deleted: a deletion still waiting for approval has nothing to purge. Request
 * of 24 September 2026.
 *
 * `onPurged` runs once the case has gone, so a record that no longer exists can
 * take the reader back to the list.
 */
export function PurgeCaseButton({ order, onPurged }: { order: JobOrder; onPurged?: () => void }) {
  const session = useSession();
  const [asking, setAsking] = useState(false);
  const purge = usePurgeCase();

  const { deletion, jobOrderNo, rowVersion } = order;
  const may =
    deletion.status === 'deleted' && (session.data?.permissions ?? []).includes('approveDeletion');
  if (!may) return null;

  return (
    <>
      <Button onClick={() => setAsking(true)}>{strings.deletion.purge}</Button>

      {asking && (
        <PurgeCaseDialog
          jobOrderNo={jobOrderNo}
          busy={purge.isPending}
          error={purge.error === null ? null : failureWords(purge.error)}
          onCancel={() => {
            setAsking(false);
            purge.reset();
          }}
          onConfirm={() =>
            purge.mutate(
              { jobOrderNo, rowVersion },
              {
                onSuccess: () => {
                  setAsking(false);
                  toast(`${jobOrderNo} ${strings.deletion.purgedToast}`);
                  onPurged?.();
                },
              },
            )
          }
        />
      )}
    </>
  );
}
